'use client'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { formatNumber } from '@/lib/utils/format'
import type { ActionResult } from '@/types/actions'
import type { StarPromptResult } from '@/types/prompt.types'

interface StarButtonProps {
  promptId: string
  isStarred: boolean
  count: number
  starPrompt: (promptId: string) => Promise<ActionResult<StarPromptResult>>
}

export function StarButton({
  promptId,
  isStarred: initialIsStarred,
  count: initialCount,
  starPrompt,
}: StarButtonProps) {
  const queryClient = useQueryClient()
  const [isStarred, setIsStarred] = useState(initialIsStarred)
  const [count, setCount] = useState(initialCount)

  const mutation = useMutation({
    mutationFn: () => starPrompt(promptId),
    onMutate: () => {
      const prevStarred = isStarred
      const prevCount = count
      setIsStarred(!isStarred)
      setCount(isStarred ? count - 1 : count + 1)
      return { prevStarred, prevCount }
    },
    onError: (_err, _vars, context) => {
      if (context) {
        setIsStarred(context.prevStarred)
        setCount(context.prevCount)
      }
    },
    onSuccess: (result) => {
      if (result.success && result.data) {
        setIsStarred(result.data.isStarred)
        setCount(result.data.count)
      }
      queryClient.invalidateQueries({ queryKey: ['prompt'] })
    },
  })

  return (
    <button
      type="button"
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
      className="group flex items-center gap-1.5 rounded-[var(--radius-md)] border border-[var(--color-border-default)] px-3 py-1.5 text-[var(--text-sm)] transition-colors hover:border-[var(--color-accent-border)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)] disabled:opacity-50"
      aria-label={`${isStarred ? 'Unlike' : 'Like'} prompt, ${count} ${count === 1 ? 'like' : 'likes'}`}
      aria-pressed={isStarred}
    >
      {/* "Like" affordance (heart). The ★ icon is reserved for the 1-5 quality
          rating; the underlying like/feed signal is unchanged. */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.svg
          key={isStarred ? 'filled' : 'empty'}
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.5, opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="h-4 w-4"
          viewBox="0 0 24 24"
          fill={isStarred ? 'var(--color-accent)' : 'none'}
          stroke={isStarred ? 'var(--color-accent)' : 'var(--color-text-secondary)'}
          strokeWidth="2"
          aria-hidden="true"
        >
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </motion.svg>
      </AnimatePresence>
      <span
        className={`min-w-[1.5rem] text-center tabular-nums ${isStarred ? 'text-[var(--color-accent-text)]' : 'text-[var(--color-text-secondary)]'}`}
        aria-hidden="true"
      >
        {formatNumber(count)}
      </span>
    </button>
  )
}
